import { useMemo } from 'react';

import { GenericProvider } from '@/features/game-context/types.ts';
import {
  ExtendableGenericEvents,
  GenericEvents,
} from '@/features/game-window-integration/providers/generic-provider/types.ts';
import { useGenericProviderHandlers } from '@/features/game-window-integration/providers/generic-provider/use-generic-provider-handlers.ts';
import { remapEvent } from '@/features/game-window-integration/providers/generic-provider/utils.ts';

export const useEventHandlersAdaptor = (provider: GenericProvider) => {
  const {
    handleGameReady,
    handleGameLoadStarted,
    handleGameLoadProgress,
    handleSpinInProgress,
    handleAutoplayStop,
    handleVolumeChange,
    handlePayloadVolumeChange,
    handleCloseGame,
    handleResetGame,
    getGameProviderFrame,
    handleNotification,
    handleNotImplemented,
    handleError,
  } = useGenericProviderHandlers(provider);

  const providerEventHandlerMapper = useMemo(
    () => ({
      [GenericEvents.ALERT_ACTION]: handleNotImplemented,
      [GenericEvents.APPLICATION_READY]: handleGameReady,
      [GenericEvents.AUTOPLAY_STOP]: handleAutoplayStop,
      [GenericEvents.BACK_TO_PARTNER]: handleCloseGame,
      [GenericEvents.CLOSE]: handleNotImplemented,
      [GenericEvents.GAME_LOAD_END]: handleNotImplemented,
      [GenericEvents.GAME_LOAD_PROGRESS]: handleGameLoadProgress,
      [GenericEvents.GAME_LOAD_START]: handleGameLoadStarted,
      [GenericEvents.INITIALIZATION_REQUEST]: handleNotImplemented,
      [GenericEvents.MACHINE_LOAD_ENDED]: handleNotImplemented,
      [GenericEvents.RESTART_GAME]: handleResetGame,
      [GenericEvents.SHOW_DEPOSIT]: handleNotImplemented,
      [GenericEvents.SOUND_OFF]: handleVolumeChange,
      [GenericEvents.SOUND_ON]: handleVolumeChange,
      [GenericEvents.TOGGLE_SOUND]: handlePayloadVolumeChange,
      [GenericEvents.SPIN_IN_PROGRESS]: handleSpinInProgress,
      [GenericEvents.SPIN_IN_PROGRESS_ENDED]: handleSpinInProgress,
      [GenericEvents.NOTIFICATION]: handleNotification,
      [GenericEvents.NOTIFICATION_HANDLED]: handleNotification,
      [GenericEvents.TOGGLE_PAYTABLE]: handleNotImplemented,
      [GenericEvents.TOGGLE_HELP]: handleNotImplemented,
      [GenericEvents.ERROR_CONNECTION]: handleError,
      [GenericEvents.ERROR_GENERIC]: handleError,
      [GenericEvents.ERROR_INSUFFICIENT_FUNDS]: handleError,
      [GenericEvents.ERROR_LIMITS]: handleError,
      [GenericEvents.ERROR_AUTHENTICATION]: handleError,
      [GenericEvents.ERROR_DELEGATION]: handleError,
    }),
    [
      handleGameReady,
      handleGameLoadStarted,
      handleGameLoadProgress,
      handleNotImplemented,
      handleAutoplayStop,
      handleSpinInProgress,
      handleCloseGame,
      handleResetGame,
      handleError,
      handleNotification,
      handleVolumeChange,
      handlePayloadVolumeChange,
    ],
  );
  return useMemo(() => {
    const eventHandler: Record<string, (...args: unknown[]) => void> =
      Object.entries(providerEventHandlerMapper).reduce(
        (acc, entry) => {
          const [originalEvent, handler] = entry;
          const wireEvent: ExtendableGenericEvents = remapEvent(
            provider,
            originalEvent as GenericEvents,
          );
          acc[wireEvent] = handler as (...args: unknown[]) => void;
          return acc;
        },
        {} as Record<string, (...args: unknown[]) => void>,
      );

    return {
      getGameProviderFrame,
      providerEventHandlerMapper: eventHandler,
    };
  }, [getGameProviderFrame, provider, providerEventHandlerMapper]);
};
